import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import {
  ChevronRightIcon, ChevronLeftIcon, CheckIcon,
  UserIcon, AcademicCapIcon, SparklesIcon,
  BookOpenIcon, GlobeAltIcon, ShieldCheckIcon,
  UserGroupIcon, CalendarDaysIcon, BriefcaseIcon,
  GlobeEuropeAfricaIcon, ChartBarIcon, MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { profileApi, roleRequestsApi } from '@/api/profile'
import WizardShell from '@/components/WizardShell'
import ReconciliationPrompt from '@/components/onboarding/ReconciliationPrompt'
import { divecenterApi, membershipApi } from '@/api/divecenter'
import { useCertifications } from '@/hooks/useCertifications'
import { useAuthStore } from '@/stores/auth.store'
import { useGeolocation } from '@/hooks/useGeolocation'
import Icon, { type HeroIcon } from '@/components/ui/Icon'
import { IcBriefcase, IcCalculator, IcCalendar, IcCamera, IcClipboardList, IcGlasses, IcGraduationCap, IcInfo, IcMap, IcPalmtree, IcPartyPopper, IcShield, IcStethoscope, IcStore, IcTornado, IcUsers, IcWaves } from '@/components/ui/icons'

// ── Persistence keys ──────────────────────────────────────────────────────────

const ONBOARDING_KEY = 'edive_onboarding_done'
const JTBD_KEY       = 'edive_jtbd_jobs'

export function markOnboardingDone() {
  localStorage.setItem(ONBOARDING_KEY, '1')
}

export function isOnboardingDone(): boolean {
  return localStorage.getItem(ONBOARDING_KEY) === '1'
}

export function getJtbdJobs(): string[] {
  try { return JSON.parse(localStorage.getItem(JTBD_KEY) ?? '[]') } catch { return [] }
}

export function setJtbdJobs(jobs: string[]) {
  localStorage.setItem(JTBD_KEY, JSON.stringify(jobs))
}

// ── Country lookup data ───────────────────────────────────────────────────────

const COUNTRIES: { code: string; nameKey: string }[] = [
  { code: 'IT', nameKey: 'wizOnboarding.countryIT' },
  { code: 'DE', nameKey: 'wizOnboarding.countryDE' },
  { code: 'FR', nameKey: 'wizOnboarding.countryFR' },
  { code: 'ES', nameKey: 'wizOnboarding.countryES' },
  { code: 'GB', nameKey: 'wizOnboarding.countryGB' },
  { code: 'CH', nameKey: 'wizOnboarding.countryCH' },
  { code: 'AT', nameKey: 'wizOnboarding.countryAT' },
  { code: 'BE', nameKey: 'wizOnboarding.countryBE' },
  { code: 'NL', nameKey: 'wizOnboarding.countryNL' },
  { code: 'PT', nameKey: 'wizOnboarding.countryPT' },
  { code: 'GR', nameKey: 'wizOnboarding.countryGR' },
  { code: 'HR', nameKey: 'wizOnboarding.countryHR' },
  { code: 'MT', nameKey: 'wizOnboarding.countryMT' },
  { code: 'CY', nameKey: 'wizOnboarding.countryCY' },
  { code: 'EG', nameKey: 'wizOnboarding.countryEG' },
  { code: 'TH', nameKey: 'wizOnboarding.countryTH' },
  { code: 'ID', nameKey: 'wizOnboarding.countryID' },
  { code: 'PH', nameKey: 'wizOnboarding.countryPH' },
  { code: 'AU', nameKey: 'wizOnboarding.countryAU' },
  { code: 'US', nameKey: 'wizOnboarding.countryUS' },
  { code: 'CA', nameKey: 'wizOnboarding.countryCA' },
  { code: 'MX', nameKey: 'wizOnboarding.countryMX' },
  { code: 'BR', nameKey: 'wizOnboarding.countryBR' },
  { code: 'AR', nameKey: 'wizOnboarding.countryAR' },
  { code: 'ZA', nameKey: 'wizOnboarding.countryZA' },
  { code: 'MV', nameKey: 'wizOnboarding.countryMV' },
  { code: 'LK', nameKey: 'wizOnboarding.countryLK' },
  { code: 'JP', nameKey: 'wizOnboarding.countryJP' },
  { code: 'RU', nameKey: 'wizOnboarding.countryRU' },
  { code: 'TR', nameKey: 'wizOnboarding.countryTR' },
  { code: 'AE', nameKey: 'wizOnboarding.countryAE' },
  { code: 'IL', nameKey: 'wizOnboarding.countryIL' },
  { code: 'MA', nameKey: 'wizOnboarding.countryMA' },
  { code: 'TN', nameKey: 'wizOnboarding.countryTN' },
  { code: 'SE', nameKey: 'wizOnboarding.countrySE' },
  { code: 'NO', nameKey: 'wizOnboarding.countryNO' },
  { code: 'DK', nameKey: 'wizOnboarding.countryDK' },
  { code: 'FI', nameKey: 'wizOnboarding.countryFI' },
  { code: 'PL', nameKey: 'wizOnboarding.countryPL' },
  { code: 'CZ', nameKey: 'wizOnboarding.countryCZ' },
  { code: 'HU', nameKey: 'wizOnboarding.countryHU' },
  { code: 'RO', nameKey: 'wizOnboarding.countryRO' },
  { code: 'SI', nameKey: 'wizOnboarding.countrySI' },
  { code: 'SK', nameKey: 'wizOnboarding.countrySK' },
  { code: 'BG', nameKey: 'wizOnboarding.countryBG' },
  { code: 'LT', nameKey: 'wizOnboarding.countryLT' },
  { code: 'LV', nameKey: 'wizOnboarding.countryLV' },
  { code: 'EE', nameKey: 'wizOnboarding.countryEE' },
  { code: 'IE', nameKey: 'wizOnboarding.countryIE' },
  { code: 'NZ', nameKey: 'wizOnboarding.countryNZ' },
  { code: 'SG', nameKey: 'wizOnboarding.countrySG' },
  { code: 'MY', nameKey: 'wizOnboarding.countryMY' },
  { code: 'KE', nameKey: 'wizOnboarding.countryKE' },
  { code: 'TZ', nameKey: 'wizOnboarding.countryTZ' },
  { code: 'MZ', nameKey: 'wizOnboarding.countryMZ' },
  { code: 'MU', nameKey: 'wizOnboarding.countryMU' },
  { code: 'RE', nameKey: 'wizOnboarding.countryRE' },
  { code: 'NC', nameKey: 'wizOnboarding.countryNC' },
  { code: 'PF', nameKey: 'wizOnboarding.countryPF' },
  { code: 'CK', nameKey: 'wizOnboarding.countryCK' },
  { code: 'FJ', nameKey: 'wizOnboarding.countryFJ' },
  { code: 'VN', nameKey: 'wizOnboarding.countryVN' },
  { code: 'KH', nameKey: 'wizOnboarding.countryKH' },
  { code: 'MM', nameKey: 'wizOnboarding.countryMM' },
  { code: 'IN', nameKey: 'wizOnboarding.countryIN' },
  { code: 'CN', nameKey: 'wizOnboarding.countryCN' },
  { code: 'KR', nameKey: 'wizOnboarding.countryKR' },
  { code: 'HK', nameKey: 'wizOnboarding.countryHK' },
  { code: 'TW', nameKey: 'wizOnboarding.countryTW' },
  { code: 'EC', nameKey: 'wizOnboarding.countryEC' },
  { code: 'CO', nameKey: 'wizOnboarding.countryCO' },
  { code: 'PA', nameKey: 'wizOnboarding.countryPA' },
  { code: 'CU', nameKey: 'wizOnboarding.countryCU' },
  { code: 'CL', nameKey: 'wizOnboarding.countryCL' },
  { code: 'PE', nameKey: 'wizOnboarding.countryPE' },
  { code: 'VE', nameKey: 'wizOnboarding.countryVE' },
  { code: 'DO', nameKey: 'wizOnboarding.countryDO' },
  { code: 'JM', nameKey: 'wizOnboarding.countryJM' },
  { code: 'BB', nameKey: 'wizOnboarding.countryBB' },
  { code: 'LC', nameKey: 'wizOnboarding.countryLC' },
  { code: 'TT', nameKey: 'wizOnboarding.countryTT' },
  { code: 'AW', nameKey: 'wizOnboarding.countryAW' },
  { code: 'BQ', nameKey: 'wizOnboarding.countryBQ' },
  { code: 'CW', nameKey: 'wizOnboarding.countryCW' },
]

// ── Country combobox ──────────────────────────────────────────────────────────

function CountrySelect({ value, onChange }: { value: string; onChange: (code: string) => void }) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [open, setOpen]   = useState(false)
  const ref               = useRef<HTMLDivElement>(null)

  const countries = COUNTRIES
    .map(c => ({ code: c.code, name: t(c.nameKey) }))
    .sort((a, b) => {
      // Italy first, then alphabetical
      if (a.code === 'IT') return -1
      if (b.code === 'IT') return 1
      return a.name.localeCompare(b.name, 'it')
    })

  const selected = countries.find(c => c.code === value)

  const filtered = query.trim()
    ? countries.filter(c =>
        c.name.toLowerCase().includes(query.toLowerCase()) ||
        c.code.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 10)
    : countries.slice(0, 12)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-left flex items-center gap-2 focus:outline-none focus:border-ocean-500 hover:border-gray-400 transition-colors"
      >
        {selected ? (
          <>
            <span className="font-medium text-gray-900">{selected.name}</span>
            <span className="text-gray-400 text-xs ml-auto">{selected.code}</span>
          </>
        ) : (
          <span className="text-gray-400">{t('wizOnboarding.selectCountry')}</span>
        )}
        <ChevronRightIcon className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 shrink-0" />
              <input
                autoFocus
                className="flex-1 bg-transparent text-sm outline-none placeholder-gray-400"
                placeholder={t('wizOnboarding.searchCountry')}
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">{t('wizOnboarding.noResults')}</p>
            ) : (
              filtered.map(c => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => { onChange(c.code); setOpen(false); setQuery('') }}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-ocean-50 transition-colors ${
                    value === c.code ? 'bg-ocean-50 text-ocean-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  <span>{c.name}</span>
                  <span className="text-xs text-gray-400">{c.code}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Step 1: Profilo completo ──────────────────────────────────────────────────

interface ProfileFormData {
  displayName: string
  bio: string
  country: string
  mobilePhone: string
  dateOfBirth: string
}

function StepProfile({ onNext, onBack, isFirstStep }: {
  onNext: (data: ProfileFormData) => void
  onBack?: () => void
  isFirstStep?: boolean
}) {
  const { t } = useTranslation()
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<ProfileFormData>()
  const geoCenter = useGeolocation()
  const country = watch('country', '')

  const { data: profile } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: () => profileApi.getMyProfile(),
    staleTime: 60_000,
  })

  useEffect(() => {
    if (!profile) return
    const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim()
    if (name) setValue('displayName', name, { shouldDirty: false })
    if ((profile as any).mobilePhone) setValue('mobilePhone', (profile as any).mobilePhone, { shouldDirty: false })
    if ((profile as any).dateOfBirth) {
      const dob = ((profile as any).dateOfBirth as string).slice(0, 10)
      setValue('dateOfBirth', dob, { shouldDirty: false })
    }
    if (profile.country) setValue('country', profile.country, { shouldDirty: false })
  }, [profile, setValue])

  useEffect(() => {
    if (!geoCenter || country) return
    const [lat, lon] = geoCenter
    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`, {
      headers: { 'Accept-Language': 'en' },
    })
      .then(r => r.json())
      .then(data => {
        const code = data?.address?.country_code?.toUpperCase()
        if (code && COUNTRIES.find(c => c.code === code)) {
          setValue('country', code, { shouldDirty: false })
        }
      })
      .catch(() => {})
  }, [geoCenter, country, setValue])

  const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-ocean-500'

  return (
    <form onSubmit={handleSubmit(onNext)} className="space-y-4">
      <p className="text-sm text-gray-500">{t('wizOnboarding.profileIntro')}</p>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">{t('wizOnboarding.displayNameLabel')} <span className="text-red-500">*</span></label>
        <input {...register('displayName', { required: true, minLength: 2 })}
          placeholder={t('wizOnboarding.displayNamePlaceholder')}
          className={inp} />
        {errors.displayName && <p className="text-xs text-red-500 mt-1">{t('wizOnboarding.displayNameError')}</p>}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">{t('wizOnboarding.bioLabel')} <span className="text-gray-400">{t('wizOnboarding.optional')}</span></label>
        <textarea {...register('bio')}
          placeholder={t('wizOnboarding.bioPlaceholder')}
          rows={2}
          maxLength={500}
          className={`${inp} resize-none`} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('wizOnboarding.mobileLabel')} <span className="text-red-500">*</span></label>
          <input
            {...register('mobilePhone', {
              required: t('wizOnboarding.phoneRequired'),
              validate: (v: string) => {
                // Conta solo le cifre, ignorando +, spazi, trattini, parentesi.
                const digits = (v ?? '').replace(/\D/g, '')
                if (digits.length === 0) return t('wizOnboarding.phoneRequired')
                // E.164: prefisso internazionale + numero nazionale = 8–15 cifre.
                if (digits.length < 8) return t('wizOnboarding.phoneTooShort')
                if (digits.length > 15) return t('wizOnboarding.phoneTooLong')
                return true
              },
            })}
            type="tel"
            inputMode="tel"
            placeholder="+39 333 000 0000"
            className={inp}
          />
          {errors.mobilePhone && <p className="text-xs text-red-500 mt-1">{errors.mobilePhone.message as string || t('wizOnboarding.phoneInvalid')}</p>}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('wizOnboarding.dobLabel')} <span className="text-red-500">*</span></label>
          <input
            {...register('dateOfBirth', { required: true })}
            type="date"
            max={new Date(Date.now() - 12 * 365.25 * 24 * 3600 * 1000).toISOString().slice(0, 10)}
            className={inp}
          />
          {errors.dateOfBirth && <p className="text-xs text-red-500 mt-1">{t('wizOnboarding.dobError')}</p>}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">{t('wizOnboarding.countryLabel')}</label>
        <CountrySelect
          value={country}
          onChange={v => setValue('country', v, { shouldDirty: true })}
        />
      </div>

      <div className="flex gap-3">
        {!isFirstStep && onBack && (
          <button type="button" onClick={onBack}
            className="px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1">
            <ChevronLeftIcon className="w-4 h-4" /> {t('wizOnboarding.back')}
          </button>
        )}
        <button type="submit"
          className="flex-1 py-3 bg-ocean-600 hover:bg-ocean-500 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2">
          {t('wizOnboarding.next')} <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    </form>
  )
}

// ── Step 2: Certificazione ────────────────────────────────────────────────────

const AGENCIES = ['PADI', 'SSI', 'CMAS/FIPSAS', 'CMAS', 'BSAC', 'NAUI', 'TDI', 'SDI', 'RAID', 'NADD', 'HSA', 'ESA', 'IDEA', 'SNSI', 'PSS', 'ASI SUB']

function StepCertification({ onNext, onBack, onSkip }: {
  onNext: (data: { agency: string; level: string; certId: string }) => void
  onBack: () => void
  onSkip: () => void
}) {
  const { t } = useTranslation()
  const [agency, setAgency] = useState<string | null>(null)
  const [certId, setCertId] = useState('')
  const certList = useCertifications()

  const agencyCerts = agency
    ? certList.filter(c => c.agency === agency && c.prerequisitiIds.length === 0)
    : []

  function handleNext() {
    if (!agency) { onSkip(); return }
    const cert = certList.find(c => c.id === certId)
    if (!cert) return
    onNext({ agency, level: cert.brevetto, certId })
  }

  const canProceed = agency === null || !!certId

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">{t('wizOnboarding.certIntro')}</p>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">{t('wizOnboarding.agencyLabel')}</label>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => { setAgency(null); setCertId('') }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              agency === null ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
            }`}>
            {t('wizOnboarding.none')}
          </button>
          {AGENCIES.map(a => (
            <button key={a} onClick={() => { setAgency(a); setCertId('') }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                agency === a ? 'bg-ocean-600 text-white border-ocean-600' : 'bg-white text-gray-600 border-gray-300 hover:border-ocean-400'
              }`}>
              {a}
            </button>
          ))}
        </div>
      </div>

      {agency !== null && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('wizOnboarding.certLabel')}</label>
          <select value={certId} onChange={e => setCertId(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-ocean-500 bg-white">
            <option value="">{t('wizOnboarding.selectCert')}</option>
            {agencyCerts.map(c => (
              <option key={c.id} value={c.id}>{c.brevetto}</option>
            ))}
            {agencyCerts.length === 0 && (
              <option disabled value="">{t('wizOnboarding.noCertAvailable')}</option>
            )}
          </select>
          {certId && (
            <div className="mt-2 p-3 bg-ocean-50 border border-ocean-200 rounded-xl text-xs text-ocean-700">
              {certList.find(c => c.id === certId)?.abilitazione}
            </div>
          )}
        </div>
      )}

      {agency === null && (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-500">
          {t('wizOnboarding.certLaterPrefix')} <strong>{t('wizOnboarding.certLaterSection')}</strong>{t('wizOnboarding.certLaterSuffix')}
        </div>
      )}

      <div className="flex gap-3">
        <button type="button" onClick={onBack}
          className="px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1">
          <ChevronLeftIcon className="w-4 h-4" /> {t('wizOnboarding.back')}
        </button>
        <button onClick={handleNext} disabled={!canProceed}
          className="flex-1 py-3 bg-ocean-600 hover:bg-ocean-500 disabled:opacity-40 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2">
          {t('wizOnboarding.next')} <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

// ── Step 3: Interessi JTBD ────────────────────────────────────────────────────

export interface JtbdJob {
  id: string
  labelKey: string
  descriptionKey: string
  emoji: HeroIcon
  icon: React.ElementType
  color: string
  quickLink?: { to: string; labelKey: string }
}

export const JTBD_JOBS: JtbdJob[] = [
  { id: 'traccia',  labelKey: 'wizOnboarding.jobTracciaLabel',  descriptionKey: 'wizOnboarding.jobTracciaDesc',  emoji: IcClipboardList, icon: BookOpenIcon,          color: 'sky',     quickLink: { to: '/app/logbook',                   labelKey: 'wizOnboarding.jobTracciaLink' } },
  { id: 'esplora',  labelKey: 'wizOnboarding.jobEsploraLabel',  descriptionKey: 'wizOnboarding.jobEsploraDesc',  emoji: IcMap, icon: GlobeAltIcon,          color: 'teal',    quickLink: { to: '/app/divesites',                 labelKey: 'wizOnboarding.jobEsploraLink' } },
  { id: 'impara',   labelKey: 'wizOnboarding.jobImparaLabel',   descriptionKey: 'wizOnboarding.jobImparaDesc',   emoji: IcGraduationCap, icon: AcademicCapIcon,       color: 'violet',  quickLink: { to: '/app/courses',                   labelKey: 'wizOnboarding.jobImparaLink' } },
  { id: 'sicuro',   labelKey: 'wizOnboarding.jobSicuroLabel',   descriptionKey: 'wizOnboarding.jobSicuroDesc',   emoji: IcShield, icon: ShieldCheckIcon,       color: 'rose',    quickLink: { to: '/app/medical',                   labelKey: 'wizOnboarding.jobSicuroLink' } },
  { id: 'connetto', labelKey: 'wizOnboarding.jobConnettoLabel', descriptionKey: 'wizOnboarding.jobConnettoDesc', emoji: IcUsers, icon: UserGroupIcon,         color: 'amber',   quickLink: { to: '/app/social',                    labelKey: 'wizOnboarding.jobConnettoLink' } },
  { id: 'gestisco', labelKey: 'wizOnboarding.jobGestiscoLabel', descriptionKey: 'wizOnboarding.jobGestiscoDesc', emoji: IcCalendar, icon: CalendarDaysIcon,      color: 'orange',  quickLink: { to: '/app/calendar',                  labelKey: 'wizOnboarding.jobGestiscoLink' } },
  { id: 'lavoro',   labelKey: 'wizOnboarding.jobLavoroLabel',   descriptionKey: 'wizOnboarding.jobLavoroDesc',   emoji: IcBriefcase, icon: BriefcaseIcon,         color: 'indigo',  quickLink: { to: '/app/instructor',                labelKey: 'wizOnboarding.jobLavoroLink' } },
  { id: 'proteggo', labelKey: 'wizOnboarding.jobProteggoLabel', descriptionKey: 'wizOnboarding.jobProteggoDesc', emoji: IcWaves, icon: GlobeEuropeAfricaIcon, color: 'emerald', quickLink: { to: '/app/biology-center/missions',   labelKey: 'wizOnboarding.jobProteggoLink' } },
]

const COLOR_MAP: Record<string, { ring: string; bg: string; text: string; check: string }> = {
  sky:     { ring: 'ring-ocean-400',     bg: 'bg-ocean-50',     text: 'text-ocean-700',     check: 'text-ocean-600' },
  teal:    { ring: 'ring-teal-400',    bg: 'bg-teal-50',    text: 'text-teal-700',    check: 'text-teal-600' },
  violet:  { ring: 'ring-violet-400',  bg: 'bg-violet-50',  text: 'text-violet-700',  check: 'text-violet-600' },
  rose:    { ring: 'ring-rose-400',    bg: 'bg-rose-50',    text: 'text-rose-700',    check: 'text-rose-600' },
  amber:   { ring: 'ring-amber-400',   bg: 'bg-amber-50',   text: 'text-amber-700',   check: 'text-amber-600' },
  orange:  { ring: 'ring-orange-400',  bg: 'bg-orange-50',  text: 'text-orange-700',  check: 'text-orange-600' },
  indigo:  { ring: 'ring-indigo-400',  bg: 'bg-indigo-50',  text: 'text-indigo-700',  check: 'text-indigo-600' },
  emerald: { ring: 'ring-emerald-400', bg: 'bg-emerald-50', text: 'text-emerald-700', check: 'text-emerald-600' },
}

// ── Step: Attiva il tuo profilo (ruolo/attività) ──────────────────────────────
// Self-service (Subacqueo/Apneista/Fotografo): attiva subito il profileType e prosegue.
// Con verifica (Medico/Responsabile centro): lo indica, l'attivazione avverrà dal profilo.
const ACTIVATE_OPTIONS = [
  { key: 'DIVER',                  icon: IcGlasses, mode: 'self'   as const, titleKey: 'profileActivation.diverTitle',        blurbKey: 'profileActivation.diverBlurb' },
  { key: 'FREEDIVER',              icon: IcTornado, mode: 'self'   as const, titleKey: 'profileActivation.freediverTitle',    blurbKey: 'profileActivation.freediverBlurb' },
  { key: 'UNDERWATER_PHOTOGRAPHER',icon: IcCamera, mode: 'self'   as const, titleKey: 'profileActivation.photographerTitle', blurbKey: 'profileActivation.photographerBlurb' },
  { key: 'DIVE_DOCTOR',            icon: IcStethoscope, mode: 'verify' as const, titleKey: 'profileActivation.doctorTitle',       blurbKey: 'profileActivation.doctorBlurb' },
  { key: 'DIVE_CENTER',            icon: IcPalmtree, mode: 'verify' as const, titleKey: 'profileActivation.centerTitle',       blurbKey: 'profileActivation.centerBlurb' },
]

function StepActivate({ onNext, onBack, onActivateSelf, onRequestDoctor }: {
  onNext: () => void
  onBack: () => void
  onActivateSelf: (type: string) => void
  onRequestDoctor: () => void
}) {
  const { t } = useTranslation()
  const [chosen, setChosen] = useState<string | null>(null)
  const chosenOpt = ACTIVATE_OPTIONS.find(o => o.key === chosen)
  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-gray-600 font-medium">{t('wizOnboarding.activateTitle')}</p>
        <p className="text-xs text-gray-400 mt-0.5">{t('wizOnboarding.activateSubtitle')}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {ACTIVATE_OPTIONS.map(o => {
          const active = chosen === o.key
          return (
            <button key={o.key} onClick={() => setChosen(o.key)}
              className={`flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                active ? 'border-ocean-500 bg-ocean-50 ring-2 ring-ocean-200' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}>
              <span className="text-xl shrink-0 mt-0.5"><Icon icon={o.icon} size="lg" /></span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-xs text-gray-800 flex items-center gap-1.5">
                  {t(o.titleKey)}
                  {o.mode === 'verify' && <span className="text-[9px] font-medium text-amber-700 bg-amber-50 border border-amber-100 rounded-full px-1 py-0.5">{t('wizOnboarding.activateVerifyBadge')}</span>}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5 leading-tight">{t(o.blurbKey)}</p>
              </div>
              {active && <CheckIcon className="w-4 h-4 shrink-0 mt-0.5 text-ocean-600" />}
            </button>
          )
        })}
      </div>
      {chosenOpt?.mode === 'verify' && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          {chosenOpt.key === 'DIVE_DOCTOR' ? t('wizOnboarding.activateDoctorHint') : t('wizOnboarding.activateCenterHint')}
        </p>
      )}
      <div className="flex gap-3 pt-1">
        <button type="button" onClick={onBack}
          className="px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1">
          <ChevronLeftIcon className="w-4 h-4" /> {t('wizOnboarding.back')}
        </button>
        <button
          onClick={() => {
            if (chosenOpt?.mode === 'self') onActivateSelf(chosenOpt.key)
            else if (chosenOpt?.key === 'DIVE_DOCTOR') onRequestDoctor()
            onNext()
          }}
          className="flex-1 py-3 bg-ocean-600 hover:bg-ocean-500 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2">
          {chosen ? t('wizOnboarding.activateContinue') : t('wizOnboarding.activateSkip')}
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

function StepJtbd({ onNext, onBack, selected, onToggle }: {
  onNext: () => void
  onBack: () => void
  selected: string[]
  onToggle: (id: string) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-gray-600 font-medium">{t('wizOnboarding.jtbdTitle')}</p>
        <p className="text-xs text-gray-400 mt-0.5">{t('wizOnboarding.jtbdSubtitle')}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {JTBD_JOBS.map(job => {
          const active = selected.includes(job.id)
          const c = COLOR_MAP[job.color]
          return (
            <button key={job.id} onClick={() => onToggle(job.id)}
              className={`flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all ${
                active ? `${c.ring} ${c.bg} ring-2` : 'border-gray-200 bg-white hover:border-gray-300'
              }`}>
              <span className="text-xl shrink-0 mt-0.5"><Icon icon={job.emoji} size="lg" /></span>
              <div className="min-w-0 flex-1">
                <p className={`font-semibold text-xs ${active ? c.text : 'text-gray-800'}`}>{t(job.labelKey)}</p>
                <p className="text-[11px] text-gray-400 mt-0.5 leading-tight">{t(job.descriptionKey)}</p>
              </div>
              {active && <CheckIcon className={`w-4 h-4 shrink-0 mt-0.5 ${c.check}`} />}
            </button>
          )
        })}
      </div>
      <div className="flex gap-3 pt-1">
        <button type="button" onClick={onBack}
          className="px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1">
          <ChevronLeftIcon className="w-4 h-4" /> {t('wizOnboarding.back')}
        </button>
        <button onClick={onNext}
          className="flex-1 py-3 bg-ocean-600 hover:bg-ocean-500 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2">
          {selected.length > 0 ? t('wizOnboarding.continueSelected', { count: selected.length }) : t('wizOnboarding.skip')}
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

// ── Step 4: Done ──────────────────────────────────────────────────────────────

function StepDone({ onFinish, selectedJobs }: {
  onFinish: () => void
  selectedJobs: string[]
}) {
  const { t } = useTranslation()
  const jobsWithLinks = JTBD_JOBS.filter(j => selectedJobs.includes(j.id) && j.quickLink).slice(0, 4)
  const defaultLinks = [
    { to: '/app/logbook',   label: t('wizOnboarding.doneLinkLogbook'),  emoji: IcClipboardList },
    { to: '/app/divesites', label: t('wizOnboarding.doneLinkSites'),    emoji: IcMap },
    { to: '/app/community', label: t('wizOnboarding.doneLinkCommunity'), emoji: IcUsers },
    { to: '/app/planning',  label: t('wizOnboarding.doneLinkPlanning'), emoji: IcCalculator },
  ]
  const links = jobsWithLinks.length >= 2
    ? jobsWithLinks.map(j => ({ to: j.quickLink!.to, label: t(j.quickLink!.labelKey), emoji: j.emoji }))
    : defaultLinks

  return (
    <div className="text-center space-y-5">
      <div className="w-16 h-16 rounded-full bg-ocean-100 flex items-center justify-center mx-auto text-3xl"><Icon icon={IcPartyPopper} size="2xl" /></div>
      <div>
        <h3 className="text-lg font-bold text-gray-900">{t('wizOnboarding.doneTitle')}</h3>
        <p className="text-sm text-gray-500 mt-1">
          {selectedJobs.length > 0
            ? t('wizOnboarding.doneSubtitleSelected')
            : t('wizOnboarding.doneSubtitleDefault')}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2 text-left">
        {links.map(({ to, label, emoji }) => (
          <button key={to}
            onClick={() => { onFinish(); window.location.href = to }}
            className="flex items-center gap-2.5 p-3 rounded-xl border border-gray-200 hover:border-ocean-400 hover:bg-ocean-50 transition-colors">
            <span className="text-xl"><Icon icon={emoji} size="lg" /></span>
            <span className="text-xs font-medium text-gray-700">{label}</span>
          </button>
        ))}
      </div>
      <button onClick={onFinish}
        className="w-full py-3 bg-ocean-600 hover:bg-ocean-500 text-white font-medium rounded-xl transition-colors">
        {t('wizOnboarding.goToDashboard')}
      </button>
    </div>
  )
}

// ── Step DC: Link diving center ───────────────────────────────────────────────

const STEP_DC = 15

function StepDiveCenter({ onDone, onBack }: { onDone: () => void; onBack: () => void }) {
  const { t } = useTranslation()
  const [q, setQ]               = useState('')
  const [results, setResults]   = useState<any[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [claiming, setClaiming]   = useState<string | null>(null)
  const [claimed, setClaimed]     = useState<any | null>(null)
  const [createMode, setCreateMode] = useState(false)
  const [form, setForm]           = useState({ name: '', city: '', country: '', phone: '', email: '', website: '' })

  async function doSearch() {
    if (!q.trim()) return
    setSearching(true)
    try {
      const data = await divecenterApi.list({ q: q.trim(), limit: 10 })
      setResults((data as any).items ?? data)
    } catch { setResults([]) }
    finally { setSearching(false) }
  }

  async function doClaim(center: any) {
    setClaiming(center.id)
    try {
      await divecenterApi.claim(center.id)
      setClaimed(center)
    } catch (e: any) {
      const msg = e?.response?.data?.error ?? t('wizOnboarding.error')
      toast.error(msg === 'Center is already claimed' ? t('wizOnboarding.centerAlreadyClaimed') : msg)
    } finally { setClaiming(null) }
  }

  async function doCreate() {
    if (!form.name || !form.city || !form.country) { toast.error(t('wizOnboarding.centerRequiredFields')); return }
    setClaiming('new')
    try {
      const center = await divecenterApi.create({ ...form, isActive: true } as any)
      setClaimed(center)
    } catch { toast.error(t('wizOnboarding.centerCreateError')) }
    finally { setClaiming(null) }
  }

  if (claimed) {
    return (
      <div className="space-y-4 text-center">
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto text-2xl"><Icon icon={IcStore} size="xl" /></div>
        <div>
          <h3 className="font-bold text-gray-900">{claimed.name}</h3>
          <p className="text-sm text-emerald-600 mt-1">{t('wizOnboarding.centerLinked')}</p>
        </div>
        <button onClick={onDone}
          className="w-full py-3 bg-ocean-600 hover:bg-ocean-500 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2">
          {t('wizOnboarding.continue')} <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    )
  }

  const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-ocean-500'

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">{t('wizOnboarding.dcSearchIntro')}</p>
      {!createMode ? (
        <>
          <div className="flex gap-2">
            <input className={`${inp} flex-1`} placeholder={t('wizOnboarding.dcSearchPlaceholder')}
              value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && doSearch()} />
            <button onClick={doSearch} disabled={searching || !q.trim()}
              className="px-4 py-2.5 bg-ocean-600 text-white rounded-lg text-sm font-medium hover:bg-ocean-500 disabled:opacity-50 transition-colors">
              {searching ? '...' : t('wizOnboarding.search')}
            </button>
          </div>
          {results !== null && (
            results.length === 0 ? (
              <div className="text-center py-4 text-sm text-gray-400">
                <p>{t('wizOnboarding.dcNoCenterFound')}</p>
                <button onClick={() => { setCreateMode(true); setForm(f => ({ ...f, name: q })) }}
                  className="mt-2 text-ocean-600 hover:underline text-xs">{t('wizOnboarding.dcCreateNew')}</button>
              </div>
            ) : (
              <div className="space-y-2 max-h-52 overflow-y-auto">
                {results.map((c: any) => (
                  <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                      <p className="text-xs text-gray-400">{[c.city, c.country].filter(Boolean).join(', ')}</p>
                    </div>
                    <button onClick={() => doClaim(c)} disabled={!!claiming}
                      className="shrink-0 px-3 py-1.5 bg-ocean-600 text-white text-xs rounded-lg hover:bg-ocean-500 disabled:opacity-50 transition-colors">
                      {claiming === c.id ? '...' : t('wizOnboarding.claim')}
                    </button>
                  </div>
                ))}
              </div>
            )
          )}
          <div className="flex gap-3 pt-2">
            <button onClick={onBack}
              className="px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1">
              <ChevronLeftIcon className="w-4 h-4" /> {t('wizOnboarding.back')}
            </button>
            <button onClick={onDone}
              className="flex-1 py-3 border border-gray-200 rounded-xl text-sm text-gray-500 hover:bg-gray-50 transition-colors">
              {t('wizOnboarding.configureLater')}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="space-y-3">
            {([
              { field: 'name',    label: t('wizOnboarding.dcFieldName'),    ph: t('wizOnboarding.dcFieldNamePh') },
              { field: 'city',    label: t('wizOnboarding.dcFieldCity'),    ph: t('wizOnboarding.dcFieldCityPh') },
              { field: 'country', label: t('wizOnboarding.dcFieldCountry'), ph: t('wizOnboarding.dcFieldCountryPh') },
              { field: 'phone',   label: t('wizOnboarding.dcFieldPhone'),   ph: '+39 ...' },
              { field: 'email',   label: t('wizOnboarding.dcFieldEmail'),   ph: 'info@...' },
              { field: 'website', label: t('wizOnboarding.dcFieldWebsite'), ph: 'https://...' },
            ] as const).map(({ field, label, ph }) => (
              <div key={field}>
                <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                <input className={inp} placeholder={ph}
                  value={(form as any)[field]}
                  onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))} />
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button onClick={() => setCreateMode(false)}
              className="px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1">
              <ChevronLeftIcon className="w-4 h-4" /> {t('wizOnboarding.back')}
            </button>
            <button onClick={doCreate} disabled={!!claiming || !form.name || !form.city || !form.country}
              className="flex-1 py-3 bg-ocean-600 hover:bg-ocean-500 disabled:opacity-40 text-white font-medium rounded-xl transition-colors">
              {claiming === 'new' ? t('wizOnboarding.creating') : t('wizOnboarding.createAndLink')}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ── Step: Collegati a un centro o associazione ────────────────────────────────

function StepConnect({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  const { t } = useTranslation()
  const [q, setQ]               = useState('')
  const [results, setResults]   = useState<any[] | null>(null)
  const [searching, setSearching] = useState(false)
  const [joined, setJoined]     = useState<any[]>([])
  const [joining, setJoining]   = useState<string | null>(null)

  async function doSearch() {
    if (!q.trim()) return
    setSearching(true)
    try {
      const data = await divecenterApi.list({ q: q.trim(), limit: 12 })
      setResults((data as any).items ?? (Array.isArray(data) ? data : []))
    } catch { setResults([]) }
    finally { setSearching(false) }
  }

  async function doJoin(center: any) {
    setJoining(center.id)
    try {
      await membershipApi.joinRequest(center.id)
      setJoined(prev => [...prev, center])
      toast.success(t('wizOnboarding.requestSentTo', { name: center.name }))
    } catch (e: any) {
      const msg = e?.response?.data?.error ?? ''
      if (msg.includes('Already a member') || e?.response?.status === 409) {
        toast(t('wizOnboarding.alreadyMember'), { icon: <Icon icon={IcInfo} size="sm" /> })
        setJoined(prev => [...prev, center])
      } else {
        toast.error(msg || t('wizOnboarding.joinError'))
      }
    } finally { setJoining(null) }
  }

  const joinedIds = new Set(joined.map((c: any) => c.id))

  const TYPE_BADGE: Record<string, string> = {
    DIVE_CENTER: 'bg-ocean-100 text-ocean-700',
    DIVE_RESORT: 'bg-teal-100 text-teal-700',
    ASSOCIATION: 'bg-violet-100 text-violet-700',
  }
  const TYPE_LABEL: Record<string, string> = {
    DIVE_CENTER: t('wizOnboarding.typeDiveCenter'),
    DIVE_RESORT: t('wizOnboarding.typeDiveResort'),
    ASSOCIATION: t('wizOnboarding.typeAssociation'),
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium text-gray-700">{t('wizOnboarding.connectTitle')}</p>
        <p className="text-xs text-gray-400 mt-0.5">{t('wizOnboarding.connectSubtitle')}</p>
      </div>

      <div className="flex gap-2">
        <input
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-ocean-500"
          placeholder={t('wizOnboarding.connectSearchPlaceholder')}
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && doSearch()}
        />
        <button
          onClick={doSearch}
          disabled={searching || !q.trim()}
          className="px-4 py-2.5 bg-ocean-600 text-white rounded-lg text-sm font-medium hover:bg-ocean-500 disabled:opacity-50 transition-colors"
        >
          {searching ? '…' : t('wizOnboarding.search')}
        </button>
      </div>

      {joined.length > 0 && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl space-y-1">
          <p className="text-xs font-semibold text-emerald-700 mb-1">{t('wizOnboarding.requestsSent')}</p>
          {joined.map((c: any) => (
            <p key={c.id} className="text-xs text-emerald-600">✓ {c.name}</p>
          ))}
        </div>
      )}

      {results !== null && (
        results.length === 0 ? (
          <p className="text-sm text-center text-gray-400 py-3">{t('wizOnboarding.connectNoResults')}</p>
        ) : (
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {results.map((c: any) => {
              const isJoined = joinedIds.has(c.id)
              const cType = c.centerType ?? 'DIVE_CENTER'
              return (
                <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${TYPE_BADGE[cType] ?? 'bg-gray-100 text-gray-600'}`}>
                        {TYPE_LABEL[cType] ?? cType}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{[c.city, c.country].filter(Boolean).join(', ')}</p>
                  </div>
                  {isJoined ? (
                    <span className="text-xs text-emerald-600 font-medium shrink-0">{t('wizOnboarding.sent')}</span>
                  ) : (
                    <button
                      onClick={() => doJoin(c)}
                      disabled={!!joining}
                      className="shrink-0 px-3 py-1.5 bg-ocean-600 text-white text-xs rounded-lg hover:bg-ocean-500 disabled:opacity-50 transition-colors"
                    >
                      {joining === c.id ? '…' : t('wizOnboarding.connectAction')}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )
      )}

      <div className="flex gap-3 pt-1">
        <button type="button" onClick={onBack}
          className="px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1">
          <ChevronLeftIcon className="w-4 h-4" /> {t('wizOnboarding.back')}
        </button>
        <button onClick={onNext}
          className="flex-1 py-3 bg-ocean-600 hover:bg-ocean-500 text-white font-medium rounded-xl transition-colors">
          {joined.length > 0 ? t('wizOnboarding.continueConnected', { count: joined.length }) : t('wizOnboarding.skip')}
        </button>
      </div>
    </div>
  )
}

// ── Step index helpers ────────────────────────────────────────────────────────
// Logical steps: 0=Profilo, 1=Brevetto, 2=Interessi, 3=Inizia (DC=15 inserted between 0 and 1)

// Steps: 0=Profilo, 1=Collegati, 2=Brevetto, 3=Interessi, 4=Inizia!

// ── Main wizard ───────────────────────────────────────────────────────────────

export default function OnboardingWizard() {
  const { t } = useTranslation()
  const navigate      = useNavigate()
  const qc            = useQueryClient()
  const isAdmin       = useAuthStore((s) => s.isAdminUser)
  const setProfileType = useAuthStore((s) => s.setProfileType)
  const [step, setStep]           = useState(0)
  const [saving, setSaving]       = useState(false)
  const [jtbdSelected, setJtbdSelected] = useState<string[]>([])
  // Riconciliazione anagrafica: prima degli step, verifichiamo se esiste già una
  // scheda a nome dell'utente e chiediamo conferma. reconChecked passa a true quando
  // l'utente ha deciso (o non c'erano candidati), sbloccando il resto del wizard.
  const [reconChecked, setReconChecked] = useState(false)
  const { data: reconProfile } = useQuery({
    queryKey: ['profile', 'me', 'recon'],
    queryFn: () => profileApi.getMyProfile(),
    staleTime: 60_000,
  })

  const profileMutation = useMutation({
    mutationFn: (data: Parameters<typeof profileApi.updateMyProfile>[0]) => profileApi.updateMyProfile(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile', 'me'] }),
  })
  const certMutation = useMutation({
    mutationFn: (data: { agency: string; certificationCode: string; certificationName: string; issuedAt: string }) =>
      profileApi.addCertification(data),
  })

  useEffect(() => {
    if (isAdmin) { markOnboardingDone(); navigate('/app/dashboard') }
  }, [isAdmin, navigate])

  if (isAdmin) return null

  async function handleProfileStep(data: ProfileFormData) {
    setSaving(true)
    try {
      await profileMutation.mutateAsync({
        displayName: data.displayName || undefined,
        bio:         data.bio || undefined,
        country:     data.country || undefined,
        mobilePhone: data.mobilePhone || undefined,
        dateOfBirth: data.dateOfBirth || undefined,
      } as any)
      setStep(1)
    } catch { toast.error(t('wizOnboarding.profileSaveError')) }
    finally { setSaving(false) }
  }

  // Attivazione self-service del profileType dallo step "Cosa vuoi fare" (no approvazione).
  function activateSelf(type: string) {
    profileMutation.mutate({ profileType: type } as any, {
      onSuccess: () => { setProfileType(type as any); toast.success(t('wizOnboarding.activateProfileDone')) },
    })
  }

  // Medico: invia la richiesta ruolo DIVE_DOCTOR (approvazione admin = "verifica").
  function requestDoctor() {
    roleRequestsApi.submit({ requestedRole: 'DIVE_DOCTOR' })
      .then(() => toast.success(t('wizOnboarding.activateDoctorSent')))
      .catch(() => toast.error(t('wizOnboarding.activateDoctorError')))
  }

  async function handleCertStep(data: { agency: string; level: string; certId: string }) {
    setSaving(true)
    try {
      await certMutation.mutateAsync({
        agency: data.agency,
        certificationCode: data.certId.toUpperCase(),
        certificationName: data.level,
        issuedAt: new Date().toISOString(),
      })
    } catch { /* non-blocking */ }
    setSaving(false)
    setStep(4)
  }

  function handleJtbdNext() {
    const toSave = jtbdSelected.length > 0 ? jtbdSelected : JTBD_JOBS.map(j => j.id)
    setJtbdJobs(toSave)
    setStep(5)
  }

  function toggleJtbd(id: string) {
    setJtbdSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  function finish() {
    markOnboardingDone()
    navigate('/app/dashboard')
  }

  const STEPS = [
    t('wizOnboarding.stepProfile'),
    t('wizOnboarding.stepActivate'),
    t('wizOnboarding.stepConnect'),
    t('wizOnboarding.stepCert'),
    t('wizOnboarding.stepInterests'),
    t('wizOnboarding.stepStart'),
  ]

  return (
    <>
    {/* Prima degli step: proponi la riconciliazione con una scheda anagrafica
        preesistente a nome dell'utente, e collega solo su sua conferma. */}
    {!reconChecked && (
      <ReconciliationPrompt
        firstName={reconProfile?.firstName}
        lastName={reconProfile?.lastName}
        onDone={() => setReconChecked(true)}
      />
    )}
    {/* Cornice condivisa (overlay + header + stepper, brand ocean). La navigazione
        resta dentro i singoli step (hideFooter); l'onboarding non è chiudibile a metà
        (hideClose). */}
    <WizardShell
      title={t('wizOnboarding.wizardTitle')}
      steps={STEPS}
      currentStep={step}
      onClose={() => {}}
      hideFooter
      hideClose
    >
      {step === 0 && (
        <StepProfile
          onNext={handleProfileStep}
          isFirstStep
        />
      )}
      {step === 1 && (
        <StepActivate
          onNext={() => setStep(2)}
          onBack={() => setStep(0)}
          onActivateSelf={activateSelf}
          onRequestDoctor={requestDoctor}
        />
      )}
      {step === 2 && (
        <StepConnect
          onNext={() => setStep(3)}
          onBack={() => setStep(1)}
        />
      )}
      {step === 3 && (
        <StepCertification
          onNext={handleCertStep}
          onBack={() => setStep(2)}
          onSkip={() => setStep(4)}
        />
      )}
      {step === 4 && (
        <StepJtbd
          selected={jtbdSelected}
          onToggle={toggleJtbd}
          onNext={handleJtbdNext}
          onBack={() => setStep(3)}
        />
      )}
      {step === 5 && (
        <StepDone onFinish={finish} selectedJobs={jtbdSelected} />
      )}
    </WizardShell>

    {saving && (
      <div className="fixed inset-0 z-[10000] bg-black/30 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    )}
    </>
  )
}
